import { Link } from "react-router-dom";
import {
  ClipboardCheck,
  ShieldCheck,
  Car,
  Sparkles,
  Check,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";
import { BrandPatternLayer } from "@/components/BrandPatternLayer";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { ContactForm } from "@/components/ContactForm";
import { Button } from "@/components/ui/button";
import { site, hotlines } from "@/lib/siteConfig";
import { commonContent } from "@/cms/cmsModel";
import { useCmsDocument } from "@/cms/CmsContentProvider";

const iconMap = { ClipboardCheck, ShieldCheck, Car, Sparkles };

// A value is a real, renderable asset when it exists and is not one of the
// "[… folgt]" placeholder tokens (which are wrapped in square brackets).
const isRealAsset = (v) => typeof v === "string" && v.length > 0 && !v.startsWith("[");

/**
 * Single reusable template shared by all four dedicated service pages.
 * It reproduces the existing AZ Gruppe visual identity (logo/header/footer via
 * the Layout, AZ line pattern, typography, colours and the subtle Reveal
 * animations) and renders fully from the CMS-ready `content` object.
 */
export const ServicePageTemplate = ({ content }) => {
  const copy = commonContent.service;
  const { locale } = useCmsDocument();
  const Icon = iconMap[content.icon] || Sparkles;
  // Optional per-page colour theme (CSS variables). When absent, every
  // `var(--svc-*, <default>)` below falls back to the shared brand palette,
  // so the other service pages render exactly as before.
  const theme = content.theme;
  // Only ever render real media — never a "[… folgt]" placeholder box.
  const hasHeroImage = isRealAsset(content.hero?.image);
  // Optional full-bleed background image for the contact/CTA section. When set,
  // it replaces the navy background + animated line pattern.
  const hasCtaBackground = isRealAsset(content.cta?.background);
  const realGallery = (content.media?.gallery || []).filter(isRealAsset);
  const realParagraphs = (content.detail?.paragraphs || []).filter(
    (p) => typeof p === "string" && !p.startsWith("[")
  );

  return (
    <div data-testid={`service-page-${content.slug}`} style={theme}>
      {/* A. HERO – full-bleed image with navy overlay --------------------- */}
      <section className="relative flex min-h-[460px] items-center overflow-hidden bg-[var(--svc-dark,#193D6D)] text-[var(--svc-invert,#F1F4F8)] lg:min-h-[560px]">
        {/* Full-bleed background image */}
        {hasHeroImage && (
          <img
            src={content.hero.image}
            alt={content.title}
            loading="eager"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        )}
        {/* Neutral dark scrim (image) keeps the left readable without tinting
            the photo blue; plain navy (no image) stays on-brand. No moving
            pattern in the hero. */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: hasHeroImage
              ? "linear-gradient(100deg, rgba(12,16,20,0.82) 0%, rgba(12,16,20,0.62) 32%, rgba(12,16,20,0.28) 60%, rgba(12,16,20,0.05) 100%)"
              : "radial-gradient(820px 380px at 82% 16%, var(--svc-accent-glow, rgba(201,154,61,0.16)), transparent 60%)",
          }}
        />
        <div className="relative az-container py-16 lg:py-20">
          <div className="max-w-2xl">
            <Reveal immediate>
              <span className="az-eyebrow" style={{ color: "var(--svc-accent, #C99A3D)" }}>
                <span className="inline-block h-px w-6 bg-current" /> {content.eyebrow}
              </span>
            </Reveal>
            <Reveal immediate delay={0.05}>
              <h1 className="mt-4 font-heading text-4xl font-bold leading-[1.08] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
                {content.title}
              </h1>
            </Reveal>
            <Reveal immediate delay={0.1}>
              <p className="mt-5 max-w-xl text-lg text-[rgba(244,246,249,0.88)]">
                {content.intro}
              </p>
            </Reveal>
            <Reveal immediate delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={`tel:${site.primaryPhoneTel}`} data-testid="service-hero-call-link">
                  <Button className="rounded-xl bg-[var(--svc-primary,#7A1516)] text-[var(--svc-invert,#F1F4F8)] hover:bg-[var(--svc-primary-hover,#941d1e)]">
                    <Phone size={16} className="mr-2" /> {copy.phoneButton}
                  </Button>
                </a>
                <Link to="/kontakt" data-testid="service-hero-contact-link">
                  <Button
                    variant="outline"
                    className="rounded-xl border-[rgba(244,246,249,0.45)] bg-transparent text-[var(--svc-invert,#F1F4F8)] hover:bg-[rgba(244,246,249,0.12)]"
                  >
                    {copy.contactButton} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* A2. HIGHLIGHTS strip (optional) ---------------------------------- */}
      {content.highlights?.length > 0 && (
        <section className="relative border-b border-[var(--svc-border,#D5DEE8)] bg-[#E7EDF5]/90">
          <div className="az-container">
            <div className="grid divide-y divide-[var(--svc-border,#D5DEE8)] sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 lg:divide-x">
              {content.highlights.map((h, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center gap-2.5 px-4 py-5 text-center"
                >
                  <Check size={18} className="flex-none text-[var(--svc-primary,#7A1516)]" />
                  <span className="font-heading text-base font-semibold text-[var(--svc-ink,#193D6D)]">
                    {h}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* B. DETAILED DESCRIPTION ------------------------------------------ */}
      <section className="relative isolate overflow-hidden bg-[#F1F4F8]/90 py-16 lg:py-24">
        <BrandPatternLayer variant="service" composition="a" />
        <div className="relative z-10 az-container">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-7">
              <SectionHeading eyebrow={copy.detailEyebrow} title={content.detail.heading} />
              <div className="mt-6 space-y-4">
                {realParagraphs.map((p, i) => (
                  <p key={i} className="text-lg leading-relaxed text-[var(--svc-body,#2f3a49)]">
                    {p}
                  </p>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="rounded-3xl border border-[var(--svc-border,#D5DEE8)] bg-white p-7 shadow-[var(--shadow-card)] lg:sticky lg:top-28">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--svc-primary,#7A1516)] text-white">
                    <Icon size={26} />
                  </span>
                  <h3 className="mt-5 font-heading text-2xl font-semibold text-[var(--svc-ink,#193D6D)]">
                    {content.title}
                  </h3>
                  <p className="mt-2 text-[var(--svc-muted,#586576)]">{content.intro}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* C. MEDIA – only rendered when real images exist ------------------ */}
      {realGallery.length > 0 && (
        <>
          <section
            className="relative isolate overflow-hidden bg-[#E7EDF5]/90 py-16 lg:py-24"
            data-testid="service-media-section"
          >
            <BrandPatternLayer variant="service" composition="b" />
            <div className="relative z-10 az-container">
              <SectionHeading
                eyebrow={content.media.eyebrow}
                title={content.media.heading}
                subtitle={content.media.subtitle}
              />
              <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {realGallery.map((g, i) => {
                  // With 5 (or 8, …) images the last one spans two columns so
                  // the grid closes without an empty cell.
                  const spans =
                    realGallery.length % 3 === 2 && i === realGallery.length - 1;
                  return (
                    <Reveal
                      key={i}
                      delay={i * 0.06}
                      className={`az-service-frame relative w-full overflow-hidden rounded-3xl shadow-[var(--shadow-card)] ${
                        spans ? "h-full min-h-[240px] sm:col-span-2" : "aspect-[4/3]"
                      }`}
                    >
                      <img
                        src={g}
                        alt={`${content.title} – ${locale === "ar" ? "صورة" : "Bild"} ${i + 1}`}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </Reveal>
                  );
                })}
              </div>
              {isRealAsset(content.media.video) && (
                <Reveal delay={0.1}>
                  <div className="mt-5 overflow-hidden rounded-3xl border border-[var(--svc-border,#D5DEE8)] shadow-[var(--shadow-card)]">
                    <video src={content.media.video} controls className="aspect-video w-full" />
                  </div>
                </Reveal>
              )}
            </div>
          </section>
        </>
      )}

      {/* D. BENEFITS ------------------------------------------------------- */}
      <section className="relative isolate overflow-hidden bg-[#F1F4F8]/90 py-16 lg:py-24">
        <BrandPatternLayer variant="service" composition="b" />
        <div className="relative z-10 az-container">
          <SectionHeading eyebrow={content.benefits.eyebrow} title={content.benefits.heading} />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {content.benefits.items.map((b, i) => (
              <Reveal key={i} delay={i * 0.06} className="h-full">
                <div className="flex h-full flex-col rounded-2xl border border-[var(--svc-border,#D5DEE8)] bg-white p-6 shadow-[var(--shadow-soft)]">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--svc-primary-soft,#7A151614)] text-[var(--svc-primary,#7A1516)]">
                    <Check size={20} />
                  </span>
                  <h3 className="mt-4 font-heading text-xl font-semibold leading-snug text-[var(--svc-ink,#193D6D)]">
                    {b.title}
                  </h3>
                  {b.text && <p className="mt-2 flex-1 text-[var(--svc-muted,#586576)]">{b.text}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* D2. SCOPE / RUNDUM-SERVICE (optional) ---------------------------- */}
      {content.scope && (
        <section
          className="relative isolate overflow-hidden bg-[#E7EDF5]/90 py-16 lg:py-24"
          data-testid="service-scope-section"
        >
          <BrandPatternLayer variant="service" composition="a" />
          <div className="relative z-10 az-container">
            <SectionHeading
              eyebrow={content.scope.eyebrow}
              title={content.scope.heading}
              subtitle={content.scope.subtitle}
            />
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {content.scope.items.map((item, i) => (
                <Reveal key={i} delay={i * 0.05} className="h-full">
                  <div className="flex h-full items-start gap-3 rounded-2xl border border-[var(--svc-border,#D5DEE8)] bg-white p-5 shadow-[var(--shadow-soft)]">
                    <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[var(--svc-primary-soft,#7A151614)] text-[var(--svc-primary,#7A1516)]">
                      <Check size={18} />
                    </span>
                    <span className="font-medium leading-snug text-[var(--svc-body,#2f3a49)]">{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* D3. STEPS / PROCESS (optional) ----------------------------------- */}
      {content.steps && (
        <section className="relative isolate overflow-hidden bg-[#F1F4F8]/90 py-16 lg:py-24">
            <BrandPatternLayer variant="service" composition="b" />
            <div className="relative z-10 az-container">
            <SectionHeading
              eyebrow={content.steps.eyebrow}
              title={content.steps.heading}
              subtitle={content.steps.subtitle}
            />
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {content.steps.items.map((s, i) => (
                <Reveal key={i} delay={i * 0.06} className="h-full">
                  <div className="flex h-full flex-col rounded-2xl border border-[var(--svc-border,#D5DEE8)] bg-white p-6 shadow-[var(--shadow-soft)]">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--svc-primary,#7A1516)] font-heading text-lg font-bold text-white">
                      {i + 1}
                    </span>
                    <h3 className="mt-4 font-heading text-lg font-semibold leading-snug text-[var(--svc-ink,#193D6D)]">
                      {s.title}
                    </h3>
                    <p className="mt-2 flex-1 text-[var(--svc-muted,#586576)]">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* E + F. CONTACT CTA + SERVICE-SPECIFIC FORM ----------------------- */}
      <section
        className="relative overflow-hidden bg-[var(--svc-dark,#193D6D)] py-16 text-[var(--svc-invert,#F1F4F8)] lg:py-24"
        data-testid="service-cta-section"
      >
        {hasCtaBackground ? (
          <img
            src={content.cta.background}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        ) : (
          <BrandPatternLayer variant="hero" composition="a" />
        )}
        <div className="relative z-10 az-container">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
            {/* CTA */}
            <div>
              <SectionHeading
                eyebrow={content.cta.eyebrow}
                title={content.cta.heading}
                subtitle={content.cta.text}
                tone="dark"
              />
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <a href={`tel:${site.primaryPhoneTel}`} data-testid="service-cta-call-link">
                  <Button className="w-full rounded-xl bg-[var(--svc-primary,#7A1516)] text-[var(--svc-invert,#F1F4F8)] hover:bg-[var(--svc-primary-hover,#941d1e)] sm:w-auto">
                    <Phone size={16} className="mr-2" /> {copy.phoneButton}
                  </Button>
                </a>
                <Link to="/kontakt" data-testid="service-cta-contact-link">
                  <Button
                    variant="outline"
                    className="w-full rounded-xl border-[rgba(244,246,249,0.35)] bg-transparent text-[var(--svc-invert,#F1F4F8)] hover:bg-[rgba(244,246,249,0.1)] sm:w-auto"
                  >
                    {copy.contactButton} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
                <a href={`mailto:${site.email}`} data-testid="service-cta-email-link">
                  <Button
                    variant="outline"
                    className="w-full rounded-xl border-[rgba(244,246,249,0.35)] bg-transparent text-[var(--svc-invert,#F1F4F8)] hover:bg-[rgba(244,246,249,0.1)] sm:w-auto"
                  >
                    <Mail size={16} className="mr-2" /> {copy.emailButton}
                  </Button>
                </a>
              </div>

              {content.cta.points?.length > 0 && (
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {content.cta.points.map((p, i) => (
                    <div
                      key={i}
                      className="rounded-2xl border border-[rgba(244,246,249,0.16)] bg-[rgba(244,246,249,0.06)] p-4"
                    >
                      <div className="flex items-center gap-2 font-heading text-base font-semibold text-[var(--svc-invert,#F1F4F8)]">
                        <Check size={16} className="flex-none text-[var(--svc-accent,#C99A3D)]" />
                        {p.title}
                      </div>
                      <p className="mt-1 text-sm text-[rgba(244,246,249,0.72)]">{p.text}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 space-y-2.5">
                {hotlines.map((h) => (
                  <a
                    key={h.id}
                    href={`tel:${h.tel}`}
                    className="flex items-center gap-3 text-sm text-[rgba(244,246,249,0.85)] transition-colors hover:text-white"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(244,246,249,0.1)] text-[var(--svc-accent,#C99A3D)]">
                      <Phone size={14} />
                    </span>
                    <span>
                      <span className="text-[rgba(244,246,249,0.6)]">{h.label}:</span> {h.number}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Service-specific form (category preset automatically) */}
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-[var(--svc-border,#D5DEE8)] bg-white p-6 shadow-[var(--shadow-card)] sm:p-8">
                <span className="az-eyebrow" style={{ color: "var(--svc-primary, #7A1516)" }}>
                  <span className="inline-block h-px w-6 bg-current" /> {content.form.eyebrow}
                </span>
                <h3 className="mt-3 font-heading text-2xl font-semibold text-[var(--svc-ink,#193D6D)]">
                  {content.form.heading}
                </h3>
                <p className="mt-1 mb-5 text-[var(--svc-muted,#586576)]">{content.form.subtitle}</p>
                <ContactForm
                  sourcePage={`service-${content.slug}`}
                  defaultCategory={content.category}
                  showCategory={false}
                  testidPrefix={`service-form-${content.slug}`}
                  submitLabel={copy.submitLabel}
                  contextLabel={`${locale === "ar" ? "المجال" : "Bereich"}: ${content.title}`}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};
